import React from 'react'

import useTranslation from '@/Hooks/UseTranslation'
import { MsProjectPreview } from '@/Services/MsProjectImportService'
import {
  CostsTab,
  ColorCodesTab,
  ProgrammingTab,
  ProjectTab,
  RelationsTab,
  RestrictionsTab,
} from './MsProjectImportStepTwoTabs'

export type PreviewTab = import('./MsProjectImportStepTwoTabs/types').PreviewTab
export type ProgrammingRow = import('./MsProjectImportStepTwoTabs/types').ProgrammingRow
export type ValidationErrorGroup = import('./MsProjectImportStepTwoTabs/types').ValidationErrorGroup

type Props = {
  activeTab: PreviewTab
  contentRef: React.RefObject<HTMLDivElement>
  errorKey: string | null
  errorTaskUids: Set<string>
  highlightedProgrammingUid: string | null
  highlightedColorCodeId: string | null
  preview: MsProjectPreview
  programmingRows: ProgrammingRow[]
  validationErrorGroups: ValidationErrorGroup[]
  onCorrectValidationError: (errorGroup: ValidationErrorGroup) => void
  onExcludeTask: (uid: string) => void
  onGoToProgrammingRow: (uid: string) => void
  onGoToColorCode: (id: string) => void
  onTabChange: (tab: PreviewTab) => void
  onUpdateTaskDate: (uid: string, field: 'start' | 'finish', value: string) => void
  onUpdateTaskId: (uid: string, value: string) => void
  onUpdateTaskName: (uid: string, name: string) => void
  onUpdateColorCode: (id: string, field: 'name' | 'color', value: string) => void
  onUpdateProject: (field: 'name' | 'startsAt' | 'endsAt', value: string) => void
  onSelectSheet: (sheetName: string) => void
}

const MsProjectImportStepTwo = ({
  activeTab,
  contentRef,
  errorKey,
  errorTaskUids,
  highlightedProgrammingUid,
  highlightedColorCodeId,
  preview,
  programmingRows,
  validationErrorGroups,
  onCorrectValidationError,
  onExcludeTask,
  onGoToProgrammingRow,
  onGoToColorCode,
  onTabChange,
  onUpdateTaskDate,
  onUpdateTaskId,
  onUpdateTaskName,
  onUpdateColorCode,
  onUpdateProject,
  onSelectSheet,
}: Props) => {
  const { t } = useTranslation()
  const previewTabs: PreviewTab[] = preview.sourceType === 'gantt'
    ? [ 'project', 'programming', ...(preview.colorCodes?.length ? [ 'colorCodes' as PreviewTab ] : []) ]
    : preview.sourceType === 'pull_planning'
      ? [ 'project', 'programming' ]
      : [ 'project', 'programming', ...(preview.colorCodes?.length ? [ 'colorCodes' as PreviewTab ] : []), ...(preview.relations.length ? [ 'relations' as PreviewTab ] : []), ...(preview.restrictions.length ? [ 'restrictions' as PreviewTab ] : []), ...(preview.costs.length ? [ 'costs' as PreviewTab ] : []) ]
  const taskCounts = (preview.tasks || []).reduce<Record<string, number>>((counts, task) => {
    if (task.colorCodeId) counts[task.colorCodeId] = (counts[task.colorCodeId] || 0) + 1
    return counts
  }, {})

  return (
    <div className="ms-project-importer-review-layout">
      <div className="ms-project-importer-tabs" role="tablist" aria-orientation="vertical">
        {previewTabs.map((tab, index) => (
          <button
            key={tab}
            type="button"
            className={`ms-project-importer-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => onTabChange(tab)}
            role="tab"
            aria-selected={activeTab === tab}
          >
            <span className="ms-project-importer-tab-step">2.{index + 1}</span>
            <span className="ms-project-importer-tab-label">{t(`MS_PROJECT_IMPORTER:Tab:${tab}`)}</span>
            <span className="ms-project-importer-tab-indicator" aria-hidden="true">›</span>
          </button>
        ))}
      </div>

      <div ref={contentRef} className="ms-project-importer-content custom-scrollbar">
        {errorKey && <div className="alert alert-danger" role="alert">{t(errorKey)}</div>}

        {activeTab === 'project' && (
          <ProjectTab preview={preview} validationErrorGroups={validationErrorGroups} onCorrectValidationError={onCorrectValidationError} onUpdateProject={onUpdateProject} onSelectSheet={onSelectSheet} />
        )}
        {activeTab === 'programming' && (
          <ProgrammingTab
            errorTaskUids={errorTaskUids}
            highlightedUid={highlightedProgrammingUid}
            preview={preview}
            rows={programmingRows}
            onExcludeTask={onExcludeTask}
            onUpdateTaskDate={onUpdateTaskDate}
            onUpdateTaskId={onUpdateTaskId}
            onUpdateTaskName={onUpdateTaskName}
            onGoToColorCode={onGoToColorCode}
          />
        )}
        {activeTab === 'colorCodes' && <ColorCodesTab colorCodes={preview.colorCodes || []} highlightedId={highlightedColorCodeId} taskCounts={taskCounts} onUpdate={onUpdateColorCode} />}
        {activeTab === 'relations' && <RelationsTab relations={preview.relations} onGoToProgrammingRow={onGoToProgrammingRow} />}
        {activeTab === 'restrictions' && <RestrictionsTab restrictions={preview.restrictions} onGoToProgrammingRow={onGoToProgrammingRow} />}
        {activeTab === 'costs' && <CostsTab costs={preview.costs} onGoToProgrammingRow={onGoToProgrammingRow} />}
      </div>
    </div>
  )
}

export default MsProjectImportStepTwo
